/**
 * src/shell/portal/components/DemoSchedulerModal.tsx
 *
 * Frictionless 1-Click Executive Demo Scheduler.
 * - Single-step scheduling for enterprise mesh and node cluster walkthroughs.
 * - Dual Daily Tracks: 1-on-1 Architecture Demo vs Interactive Group Walkthrough + Q&A.
 * - Interactive calendar slot picker and automated confirmation receipt.
 */

import React, { useState } from 'react';
import { X, Calendar, Clock, CheckCircle2, User, Users, Download, Sparkles } from 'lucide-react';

export interface DemoSchedulerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserDisplayName?: string;
  currentUserHandle?: string;
}

const AVAILABLE_SLOTS = [
  { time: '10:00 AM SGT (UTC+8)', track: '1-on-1 Architecture Deep-Dive' },
  { time: '02:00 PM SGT (UTC+8)', track: 'Interactive Group Walkthrough + Q&A' },
  { time: '05:00 PM SGT (UTC+8)', track: '1-on-1 Executive Mesh Demo' },
  { time: '08:00 PM SGT (UTC+8)', track: 'DePIN Hardware & Relay Node Setup' },
];

export const DemoSchedulerModal: React.FC<DemoSchedulerModalProps> = ({
  isOpen,
  onClose,
  currentUserDisplayName = 'Sovereign Visitor',
  currentUserHandle = 'guest.herx',
}) => {
  const [selectedTrack, setSelectedTrack] = useState<'1-on-1' | 'group'>('1-on-1');
  const [selectedSlot, setSelectedSlot] = useState<string>(AVAILABLE_SLOTS[0].time);
  const [contactTarget, setContactTarget] = useState<string>(
    currentUserHandle !== 'guest.herx' ? currentUserHandle : ''
  );
  const [isBooked, setIsBooked] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleBook = (e: React.FormEvent) => {
    e.preventDefault();
    setIsBooked(true);
    // Automatically dispatch calendar invitation (.ics)
    setTimeout(() => {
      handleDownloadIcs();
    }, 400);
  };

  const handleDownloadIcs = () => {
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//HERX//Sovereign Mesh Architecture Demo//EN
BEGIN:VEVENT
UID:${Date.now()}@herx.org
DTSTAMP:20260903T000000Z
DTSTART:20260904T020000Z
DTEND:20260904T030000Z
SUMMARY:HERX Sovereign Mesh Enterprise Architecture Walkthrough
DESCRIPTION:Executive walkthrough of the Multi-Protocol Sovereign Mesh Node Cluster, DePIN Relays, and Air-Gapped Workspaces.
LOCATION:https://mesh.herx.org/demo
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'herx-mesh-demo.ics');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#0c101a',
          border: '1px solid #1e293b',
          borderRadius: 18,
          width: '100%',
          maxWidth: 480,
          color: '#f8fafc',
          boxShadow: '0 25px 50px rgba(0,0,0,0.7)',
          overflow: 'hidden',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, #111726 0%, #0c101a 100%)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Calendar size={16} color="#ffffff" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Schedule Architecture Demo</div>
              <div style={{ fontSize: 11, color: '#38bdf8', fontWeight: 600 }}>Zero-Form 1-Click Reservation</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>

        {!isBooked ? (
          <form onSubmit={handleBook} style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Track Selector */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: 8 }}>
                SELECT WALKTHROUGH TRACK
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div
                  onClick={() => setSelectedTrack('1-on-1')}
                  style={{
                    background: selectedTrack === '1-on-1' ? 'rgba(56, 189, 248, 0.15)' : '#070a10',
                    border: selectedTrack === '1-on-1' ? '1px solid #38bdf8' : '1px solid #1e293b',
                    borderRadius: 10,
                    padding: '12px 14px',
                    cursor: 'pointer',
                  }}
                >
                  <User size={18} color={selectedTrack === '1-on-1' ? '#38bdf8' : '#64748b'} />
                  <div style={{ fontWeight: 700, fontSize: 13, marginTop: 6, color: selectedTrack === '1-on-1' ? '#f8fafc' : '#94a3b8' }}>
                    1-on-1 Executive
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    Private tailored infrastructure &amp; DR consult
                  </div>
                </div>

                <div
                  onClick={() => setSelectedTrack('group')}
                  style={{
                    background: selectedTrack === 'group' ? 'rgba(56, 189, 248, 0.15)' : '#070a10',
                    border: selectedTrack === 'group' ? '1px solid #38bdf8' : '1px solid #1e293b',
                    borderRadius: 10,
                    padding: '12px 14px',
                    cursor: 'pointer',
                  }}
                >
                  <Users size={18} color={selectedTrack === 'group' ? '#38bdf8' : '#64748b'} />
                  <div style={{ fontWeight: 700, fontSize: 13, marginTop: 6, color: selectedTrack === 'group' ? '#f8fafc' : '#94a3b8' }}>
                    Group Walkthrough
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    Interactive live demo + engineering Q&amp;A
                  </div>
                </div>
              </div>
            </div>

            {/* Time Slot Picker */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: 8 }}>
                AVAILABLE DAILY TIME SLOTS
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {AVAILABLE_SLOTS.map((slot) => {
                  const isSelected = selectedSlot === slot.time;
                  return (
                    <div
                      key={slot.time}
                      onClick={() => setSelectedSlot(slot.time)}
                      style={{
                        background: isSelected ? '#162032' : '#070a10',
                        border: isSelected ? '1px solid #0284c7' : '1px solid #1e293b',
                        borderRadius: 8,
                        padding: '10px 14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Clock size={14} color={isSelected ? '#38bdf8' : '#64748b'} />
                        <span style={{ fontWeight: 600, fontSize: 13, color: isSelected ? '#38bdf8' : '#f8fafc' }}>
                          {slot.time}
                        </span>
                      </div>
                      <span style={{ fontSize: 11, color: '#64748b' }}>{slot.track}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contact Handle / Target */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
                YOUR CONTACT HANDLE OR CALENDAR EMAIL
              </label>
              <input
                type="text"
                value={contactTarget}
                onChange={(e) => setContactTarget(e.target.value)}
                placeholder="e.g. hercules@herx.org or @hercules.herx"
                required
                style={{
                  width: '100%',
                  background: '#070a10',
                  border: '1px solid #1e293b',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#ffffff',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                border: 'none',
                borderRadius: 8,
                padding: '12px 16px',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                marginTop: 4,
              }}
            >
              <Sparkles size={16} /> Confirm Reservation
            </button>
          </form>
        ) : (
          <div style={{ padding: 26, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid #10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircle2 size={28} color="#34d399" />
            </div>

            <div>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Demo Reserved!</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                Your {selectedTrack === '1-on-1' ? '1-on-1 Executive Architecture Demo' : 'Group Walkthrough'} has been scheduled for:
              </div>
              <div style={{ fontWeight: 700, color: '#38bdf8', fontSize: 14, marginTop: 4 }}>
                {selectedSlot}
              </div>
            </div>

            <div style={{ background: '#070a10', border: '1px solid #1e293b', borderRadius: 8, padding: 12, width: '100%', fontSize: 12, color: '#94a3b8', textAlign: 'left' }}>
              ✓ Notification dispatched to: <span style={{ color: '#f8fafc', fontWeight: 600 }}>{contactTarget}</span><br />
              ✓ Private access pass added to your notification drawer<br />
              ✓ Direct room link: <span style={{ color: '#38bdf8', fontWeight: 600 }}>https://mesh.herx.org/demo</span>
            </div>

            <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 8 }}>
              <button
                type="button"
                onClick={handleDownloadIcs}
                style={{
                  flex: 1,
                  background: 'rgba(56, 189, 248, 0.1)',
                  border: '1px solid #0284c7',
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: '#38bdf8',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Download size={14} /> Download .ics
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  background: '#1e293b',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: '#f8fafc',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
